import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'antd';
import { logout } from '@/api/login';
import { removeItemWithExpiry } from '@wavvest/shared-utils';
import { useAppDispatch } from '@/store/hooks';
import { resetUser, resetFeatureFlags } from '@/store/features/userinfo/userinfoSlice';
import { resetCanvas } from '@/store/features/canvas/canvasSlice';
import { clearAllCachedInvestorData } from '@/store/features/aggregatedInvestorData/aggregatedInvestorDataSlice';
import { resetInvestors } from '@/store/features/investors/investorsSlice';
import { resetApex } from '@/store/features/userinfo/apexSlice';
import { resetPlaid } from '@/store/features/userinfo/plaidSlice';
import canvasWebSocket from '@/services/websocket-service';
import { clearSentryUser } from '@/sentry';

interface UseIdleTimeoutOptions {
  timeout?: number;
  warningTime?: number;
  enabled?: boolean;
  events?: string[];
}

type ModalInstance = ReturnType<typeof Modal.warning>;

export const useIdleTimeout = (options: UseIdleTimeoutOptions = {}) => {
  const {
    timeout = 30 * 60 * 1000,
    warningTime = 2 * 60 * 1000,
    enabled = true, //set to false when testing
    events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click', 'mousemove'],
  } = options;

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const timeoutIdRef = useRef<ReturnType<typeof setTimeout>>();
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval>>();
  const lastActivityRef = useRef<number>(Date.now());
  const warningModalRef = useRef<ModalInstance | null>(null);
  const resetTimerRef = useRef<(() => void) | null>(null);

  const performLogout = useCallback(async () => {
    try {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = undefined;
      }

      if (warningModalRef.current) {
        warningModalRef.current.destroy();
        warningModalRef.current = null;
      }

      await logout();
    } catch (error) {
      console.error('Logout API failed:', error);
    } finally {
      clearSentryUser();
      removeItemWithExpiry('access_token');
      removeItemWithExpiry('user');

      dispatch(resetFeatureFlags());
      dispatch(resetUser());
      dispatch(resetCanvas());
      dispatch(resetInvestors());
      dispatch(clearAllCachedInvestorData());
      dispatch(resetApex());
      dispatch(resetPlaid());

      if (canvasWebSocket.getConnectionStatus()) {
        canvasWebSocket.disconnect();
      }

      navigate('/');
    }
  }, [dispatch, navigate]);

  const showWarning = useCallback(() => {
    if (warningModalRef.current) return;

    const deadline = Date.now() + warningTime;
    let secondsRemaining = Math.ceil(warningTime / 1000);

    warningModalRef.current = Modal.warning({
      title: 'Session Expiring Soon',
      content: `You have been inactive for ${Math.floor(timeout / 60000)} minutes. Your session will expire in ${secondsRemaining} seconds. Click "Continue" to stay logged in.`,
      okText: 'Continue',
      onOk: () => {
        warningModalRef.current = null;
        resetTimerRef.current?.();
      },
      closable: false,
      maskClosable: false,
    });

    countdownIntervalRef.current = setInterval(() => {
      const currentNow = Date.now();
      secondsRemaining = Math.ceil((deadline - currentNow) / 1000);

      if (!warningModalRef.current) {
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = undefined;
        }
        return;
      }

      if (secondsRemaining <= 0) {
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = undefined;
        }
        performLogout();
      } else {
        warningModalRef.current.update({
          content: `You have been inactive for ${Math.floor(timeout / 60000)} minutes. Your session will expire in ${secondsRemaining} seconds. Click "Continue" to stay logged in.`,
        });
      }
    }, 1000);
  }, [warningTime, timeout, performLogout]);

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();

    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = undefined;
    }

    if (warningModalRef.current) {
      warningModalRef.current.destroy();
      warningModalRef.current = null;
    }

    const waitTime = timeout - warningTime;
    if (waitTime <= 0) {
      console.warn('useIdleTimeout: warningTime must be less than timeout');
      return;
    }

    timeoutIdRef.current = setTimeout(() => {
      showWarning();
    }, waitTime);
  }, [timeout, warningTime, showWarning]);

  resetTimerRef.current = resetTimer;

  const handleActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastActivityRef.current < 1000) return;

    resetTimer();
  }, [resetTimer]);

  const handleStorageChange = useCallback(
    (e: StorageEvent) => {
      if (e.key === 'access_token' && e.newValue === null) {
        performLogout();
      }

      if (e.key === 'last_activity') {
        const lastActivity = parseInt(e.newValue || '0');
        if (lastActivity > lastActivityRef.current) {
          resetTimer();
        }
      }
    },
    [performLogout, resetTimer],
  );

  useEffect(() => {
    if (!enabled) return;

    resetTimer();

    events.forEach((event) => {
      window.addEventListener(event, handleActivity, {
        passive: true,
      } as AddEventListenerOptions);
    });

    window.addEventListener('storage', handleStorageChange);

    const syncInterval = setInterval(() => {
      localStorage.setItem('last_activity', lastActivityRef.current.toString());
    }, 5000);

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(syncInterval);

      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      if (warningModalRef.current) {
        warningModalRef.current.destroy();
      }
    };
  }, [enabled, events, handleActivity, handleStorageChange, resetTimer]);

  return {
    resetTimer,
    performLogout,
  };
};
